// Space, Group and App icons, asked of the extension by kind and id and kept
// for the life of the page. They never go through the server: a data URL in a
// LiveView assign would ride every diff (see `payload_hygiene_test.exs`).

// `kind:id` → data URL, or null when the extension has no icon for it.
const icons = new Map()
// `kind:id` → the request in flight, so a list of rows asks once per entity.
const pending = new Map()

// Asked at twice the largest size it is shown at (20 px in the composer pill);
// CSS scales it down for the 16 px history rows.
export const ICON_REQUEST_SIZE = 32

// About one row beyond the visible edge, so icons are ready as rows scroll in.
// `scrollMargin` (Chromium 120+) grows the pickers' own scroll boxes;
// `rootMargin` only the viewport.
export const ICON_VIEW_MARGIN = '40px'

// The one observer every icon on the page shares, and what to do when each
// element it watches comes into view.
let observer = null
const onVisible = new Map()

/**
 * Shows an entity's icon on `el`: at once when the page already has it,
 * otherwise once `el` is on screen and the extension has answered. `show` is
 * called only with an image data URL, never with null.
 * @param {Element} el - The icon's element, watched until it is on screen.
 * @param {string} kind - `space`, `group` or `app`.
 * @param {string} id - The entity's Wavebox id.
 * @param {Object} deps - As for `loadEntityIcon`.
 * @param {(dataUrl: string) => void} show - Sets the icon.
 * @returns {() => void} Stops watching and drops a late answer.
 */
export function watchEntityIcon (el, kind, id, deps, show) {
  if (!kind || !id) { return () => {} }
  const key = `${kind}:${id}`
  if (icons.has(key)) {
    const dataUrl = icons.get(key)
    if (dataUrl) { show(dataUrl) }
    return () => {}
  }

  let stopped = false
  whenVisible(el, () => {
    loadEntityIcon(kind, id, deps).then((dataUrl) => {
      if (!stopped && dataUrl) { show(dataUrl) }
    })
  })
  return () => {
    stopped = true
    unwatch(el)
  }
}

/**
 * The icon for one entity, or null. Answers from the page's cache, joins a
 * request already in flight, and asks the extension only when `enabled()`.
 * A failed request is not cached, so a later row may ask again; a null reply
 * (unknown id, no icon) is.
 * @param {string} kind - `space`, `group` or `app`.
 * @param {string} id - The entity's Wavebox id.
 * @param {Object} deps - How to ask the extension, and whether it can answer.
 * @param {(kind: string, id: string, size: number) => Promise<*>} deps.request - Asks the extension.
 * @param {() => boolean} deps.enabled - Whether the extension can answer.
 * @returns {Promise<string | null>}
 */
export function loadEntityIcon (kind, id, { request, enabled }) {
  if (!kind || !id || !enabled()) { return Promise.resolve(null) }

  const key = `${kind}:${id}`
  if (icons.has(key)) { return Promise.resolve(icons.get(key)) }
  if (pending.has(key)) { return pending.get(key) }

  const promise = Promise.resolve()
    .then(() => request(kind, id, ICON_REQUEST_SIZE))
    .then((reply) => {
      const dataUrl = imageDataUrl(reply?.dataUrl)
      icons.set(key, dataUrl)
      return dataUrl
    })
    .catch(() => null)
    .finally(() => pending.delete(key))

  pending.set(key, promise)
  return promise
}

/** Forget every icon and stop watching every element (tests). */
export function clearEntityIcons () {
  icons.clear()
  pending.clear()
  observer?.disconnect()
  observer = null
  onVisible.clear()
}

function whenVisible (el, callback) {
  if (typeof IntersectionObserver === 'undefined') {
    callback()
    return
  }
  if (!observer) {
    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const visible = entry.isIntersecting && onVisible.get(entry.target)
        if (visible) {
          unwatch(entry.target)
          visible()
        }
      }
    }, { rootMargin: ICON_VIEW_MARGIN, scrollMargin: ICON_VIEW_MARGIN })
  }
  unwatch(el)
  onVisible.set(el, callback)
  observer.observe(el)
}

function unwatch (el) {
  if (onVisible.delete(el)) {
    observer?.unobserve(el)
  }
}

function imageDataUrl (value) {
  return typeof value === 'string' && /^data:image\//i.test(value) ? value : null
}
